
/**
 * Utility per la condivisione e la stampa delle valutazioni
 */

import { ValuationResult, Metal, Purity, Unit } from "@/lib/pricing";
import { toast } from "sonner";

interface ValuationData {
  result: ValuationResult;
  selectedMetal: Metal;
  goldPurity?: Purity;
  silverPurity?: Purity;
  weight: string;
  unit: Unit;
}

/**
 * Crea il testo della valutazione per la condivisione
 */
export function createValuationText(data: ValuationData): string {
  const { result, selectedMetal, weight, unit } = data;
  const currentPurity = selectedMetal === 'ORO' 
    ? data.goldPurity 
    : data.silverPurity;
    
  // Formattazione della purezza per la visualizzazione
  let displayPurity = currentPurity as string;
  
  if (selectedMetal === 'ORO') {
    // Mantenere il formato originale per l'oro
  } else if (selectedMetal === 'ARGENTO') {
    if (currentPurity === 'PURO') displayPurity = '999‰';
    else if (currentPurity === '925') displayPurity = '925‰';
    else if (currentPurity === '800') displayPurity = '800‰';
  }

  return `Valutazione Metalli Preziosi - Gildy
  
Metallo: ${selectedMetal}
Purezza: ${displayPurity}
Peso: ${weight} ${unit}
Prezzo di mercato: €${result.purifiedPrice.toFixed(2)}/g

Valore stimato: ${result.formattedPrice}

Valutazione generata tramite Gildy - www.gildy.it`;
}

/**
 * Condivide la valutazione tramite l'API Web Share se disponibile,
 * altrimenti copia negli appunti
 */
export async function shareValuation(data: ValuationData): Promise<void> {
  const text = createValuationText(data);
  
  try {
    if (navigator.share) {
      await navigator.share({
        title: 'Valutazione Metalli Preziosi - Gildy',
        text: text
      });
      toast.success('Valutazione condivisa');
    } else {
      await navigator.clipboard.writeText(text);
      toast.success('Valutazione copiata negli appunti');
    }
  } catch (error) {
    console.error('Errore durante la condivisione:', error);
    
    // Fallback alla copia negli appunti
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Valutazione copiata negli appunti');
    } catch (clipboardError) {
      toast.error('Impossibile condividere la valutazione');
    }
  }
}

/**
 * Stampa la valutazione in una nuova finestra
 */
export function printValuation(data: ValuationData): void {
  const { result, selectedMetal, weight, unit } = data;
  const currentPurity = selectedMetal === 'ORO' ? data.goldPurity : data.silverPurity;
  
  // Formattazione della purezza per la visualizzazione nella stampa
  let displayPurity = currentPurity as string;
  
  if (selectedMetal === 'ORO') {
    // Mantenere il formato originale per l'oro
  } else if (selectedMetal === 'ARGENTO') {
    if (currentPurity === 'PURO') displayPurity = '999‰';
    else if (currentPurity === '925') displayPurity = '925‰';
    else if (currentPurity === '800') displayPurity = '800‰';
  }
  
  const currentTimestamp = new Date().toLocaleString('it-IT');
  
  // Creiamo un contenuto HTML da stampare
  const printContent = `
    <!DOCTYPE html>
    <html lang="it">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Valutazione Metalli - Gildy</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.5; }
        .container { max-width: 600px; margin: 0 auto; }
        .header { text-align: center; margin-bottom: 30px; }
        .logo { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
        .title { font-size: 20px; margin-bottom: 20px; }
        .result { font-size: 32px; font-weight: bold; text-align: center; margin: 20px 0; }
        .details { border-top: 1px solid #ddd; margin-top: 20px; padding-top: 20px; }
        .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .label { font-weight: bold; }
        .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
        @media print {
          body { margin: 0; }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="logo">Gildy</div>
          <div class="title">Valutazione Metalli Preziosi</div>
        </div>
        
        <div class="result">${result.formattedPrice}</div>
        
        <div class="details">
          <div class="row">
            <span class="label">Metallo:</span>
            <span>${selectedMetal}</span>
          </div>
          <div class="row">
            <span class="label">Purezza:</span>
            <span>${displayPurity}</span>
          </div>
          <div class="row">
            <span class="label">Peso:</span>
            <span>${weight} ${unit}</span>
          </div>
          <div class="row">
            <span class="label">Prezzo di mercato:</span>
            <span>€${result.purifiedPrice.toFixed(2)}/g</span>
          </div>
        </div>
        
        <div class="footer">
          <p>Valutazione generata il ${currentTimestamp}</p>
          <p>www.gildy.it</p>
        </div>
      </div>
    </body>
    </html>
  `;
  
  // Apri una nuova finestra e stampa
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Ritardo per consentire il caricamento dei contenuti
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
    
    toast.success('Preparazione stampa in corso...');
  } else {
    toast.error('Il browser ha bloccato l\'apertura della finestra di stampa');
  }
}
